import { useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';

/**
 * Google One Tap Sign-In Component
 * Provides seamless authentication with minimal user friction
 * - Shows small prompt in corner (not intrusive popup)
 * - One-click sign-in for new users
 * - Zero-click sign-in for returning users
 * - Falls back gracefully if One Tap is unavailable
 */
const GoogleOneTap = ({ onSuccess, disabled = false }) => {
  const initialized = useRef(false);

  useEffect(() => {
    // Don't initialize if disabled or already initialized
    if (disabled || initialized.current) return;

    // Check if Google One Tap library is loaded
    if (!window.google || !window.google.accounts) {
      console.log('⚠️ Google One Tap library not loaded yet');
      return;
    }

    // Get OAuth client ID from environment or Firebase config
    // You can find this in: https://console.cloud.google.com/apis/credentials
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      console.warn('⚠️ Google One Tap: REACT_APP_GOOGLE_CLIENT_ID not configured');
      console.warn('Add your OAuth 2.0 Client ID to .env file to enable One Tap sign-in');
      return;
    }

    try {
      // Initialize Google One Tap
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: true, // Automatically sign in returning users
        cancel_on_tap_outside: true,
        context: 'signin',
      });

      // Display the One Tap prompt
      // This shows a small, non-intrusive prompt in the corner
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          console.log('ℹ️ One Tap not displayed:', notification.getNotDisplayedReason());
        } else if (notification.isSkippedMoment()) {
          console.log('ℹ️ One Tap skipped:', notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          console.log('ℹ️ One Tap dismissed:', notification.getDismissedReason());
        }
      });

      initialized.current = true;
      console.log('✅ Google One Tap initialized');
    } catch (error) {
      console.error('❌ Error initializing Google One Tap:', error);
    }

    // Cleanup function
    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [disabled]);

  /**
   * Handle the credential response from Google One Tap
   * Converts the JWT credential to Firebase auth
   */
  const handleCredentialResponse = async (response) => {
    try {
      console.log('🔐 One Tap credential received');

      // Create Firebase credential from Google JWT token
      const credential = GoogleAuthProvider.credential(response.credential);

      // Sign in to Firebase with the credential
      const result = await signInWithCredential(auth, credential);
      
      console.log('✅ One Tap sign-in successful:', result.user.email);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result.user);
      }
    } catch (error) {
      console.error('❌ One Tap sign-in error:', error);
      
      // Don't show error to user - they can still use regular sign-in
      // This makes One Tap a transparent enhancement, not a required feature
    }
  };

  // This component doesn't render anything visible
  // The One Tap UI is managed by Google's library
  return null;
};

export default GoogleOneTap;

